import { useState, useEffect, useCallback } from 'react';
import { SettingsService, UserSettings, defaultSettings } from '../services/settingsService';
import { useAuth } from '../contexts/AuthContext';

export const useSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les paramètres de l'utilisateur
  const loadSettings = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const userSettings = await SettingsService.getUserSettings(user.id);
      setSettings(userSettings);
    } catch (err) {
      setError('Erreur lors du chargement des paramètres');
      console.error('Erreur chargement paramètres:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Mettre à jour un paramètre spécifique
  const updateSetting = useCallback(async (key: keyof UserSettings, value: any) => {
    if (!user?.id) return false;
    
    setError(null);
    
    try {
      const success = await SettingsService.updateUserSettings(user.id, { [key]: value });
      if (success) {
        setSettings(prev => ({ ...prev, [key]: value }));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erreur lors de la mise à jour');
      console.error('Erreur mise à jour paramètre:', err);
      return false;
    }
  }, [user?.id]);

  // Mettre à jour plusieurs paramètres
  const updateSettings = useCallback(async (newSettings: Partial<UserSettings>) => {
    if (!user?.id) return false;
    
    setLoading(true);
    setError(null);
    
    try {
      const success = await SettingsService.updateUserSettings(user.id, newSettings);
      if (success) {
        setSettings(prev => ({ ...prev, ...newSettings }));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erreur lors de la mise à jour des paramètres');
      console.error('Erreur mise à jour paramètres:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Réinitialiser les paramètres
  const resetSettings = useCallback(async () => {
    if (!user?.id) return false;
    
    setLoading(true);
    setError(null);
    
    try {
      const success = await SettingsService.resetUserSettings(user.id);
      if (success) {
        setSettings(defaultSettings);
        return true;
      }
      return false;
    } catch (err) {
      setError('Erreur lors de la réinitialisation');
      console.error('Erreur réinitialisation paramètres:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Exporter les données
  const exportData = useCallback(async () => {
    if (!user?.id) return null;
    
    setError(null);
    
    try {
      return await SettingsService.exportUserData(user.id);
    } catch (err) {
      setError('Erreur lors de l\'export des données');
      console.error('Erreur export données:', err);
      return null;
    }
  }, [user?.id]);

  // Supprimer le compte
  const deleteAccount = useCallback(async () => {
    if (!user?.id) return false;
    
    setLoading(true);
    setError(null);
    
    try {
      return await SettingsService.deleteUserData(user.id);
    } catch (err) {
      setError('Erreur lors de la suppression du compte');
      console.error('Erreur suppression compte:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Charger les paramètres au montage du composant
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    loading,
    error,
    updateSetting,
    updateSettings,
    resetSettings,
    exportData,
    deleteAccount,
    refreshSettings: loadSettings
  };
};
